import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {
    DialogButtonClickHandleResultEnum,
    IModalDialogHandler,
    ISimpleTextModalButtonOptions
} from "../../dialog/dialog.service.interface";
import {GeoFreezePopupComponent} from "../../../components/geo/geo-freeze/geo-freeze-popup.component";
import {
    PRIORITY_LEVEL,
    PRIORITY_LEVEL_ZINDEX_MAPPER
} from "../../../../sdk/src/services/overlay_manager/overlay_manager_levels";
import React from "react";
import {
    GeoLocationGameRestrictionPopupComponent
} from "../../../components/geo/game-restriction-popup/geo-location-game-restriction-popup.component";
import {NullableUndefinedString} from "../../../../common/types/nullable-types";
import {Check} from "@sparkware/app-utils";
import _ from "../../../../sdk/src/utils/underscore";
import {CHANNEL_SOURCE} from "../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import {IModalDialogButtonsComponentProps} from "../../../components/modal-dialog/modal-dialog-actions.component";
import {GeoAnalyticsUserAction, IGeoLocationUserActionAnalytics} from "../geo-location-analytics";

export enum GeoKickDialogResult {
    Logout,
    Retry,
    ContactUs,
    OK
}

export enum GeoKickDialogButtons {
    OK,
    RetryContactUs,
    LogoutContactUs
}

export enum GeoGameRestrictedDialogButton {
    OK,
    LogoutContactUs
}

export class GeoLocationDialogsFactory {
    constructor(private readonly services: IServiceFactoryExtended,
                private readonly analytics: IGeoLocationUserActionAnalytics) {
    }

    showGeoRestrictionPopup(options: {buttons: GeoGameRestrictedDialogButton;}): void {
        const services = this.services;
        let dialogButtons: IModalDialogButtonsComponentProps;

        if(options.buttons === GeoGameRestrictedDialogButton.OK) {
            dialogButtons = {
                primaryAction: {
                    text: this.services.translation.translate("OK"),
                    onClick: async () => {
                        this.analytics.reportUserAction(GeoAnalyticsUserAction.OK);
                        await this.services.user.logout();
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                },
            }
        } else {
            dialogButtons = {
                primaryAction: {
                    text: this.services.translation.translate("LOGOUT"),
                    onClick: async () => {
                        this.analytics.reportUserAction(GeoAnalyticsUserAction.Logout);
                        await this.services.user.logout();
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                },
                secondaryAction: {
                    text: this.services.translation.translate("CONTACT"),
                    onClick: async () => {
                        this.analytics.reportUserAction(GeoAnalyticsUserAction.ContactUs);
                        await this.services.appLauncher.openContactUs({
                            businessCorrelationID: _.getGuid(),
                            channel: {
                                source: CHANNEL_SOURCE.geoLocation,
                                area: AreaType.button
                            }
                        });
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                }
            };
        }


        this.analytics.reportUserAction(GeoAnalyticsUserAction.ShowGameRestrictedPopup);


        this.services.dialog.showModal(dialogHandler =>
                <GeoLocationGameRestrictionPopupComponent
                    headerText1={services.translation.translate("GEO_GAME_RESTRICTION_TOP_TEXT")}
                    headerText2={services.translation.translate("GEO_GAME_RESTRICTION_MIDDLE_TEXT")}
                    dialogHandler={dialogHandler}
                    buttons={dialogButtons}
                />, {
                title: services.translation.translate("GEO_DIALOG_STANDARD_TITLE"),
                icon: services.theme.currentTheme.icons.warningSignIcon,
                forceTransparentBackground: true,
                disableFullscreenMobile: true,
                forceCentralization: true,
                mobileCenterHeader: true,
                isHighPriority:  true,
                hideCloseButton: false,
                forceZOrder: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.GEO_FREEZE_WINDOW] - 1,
                shouldBeClosedOnlyByUser: true
            }
        );

    }

    showGeoProgressIndicator(): IModalDialogHandler {
        this.analytics.reportUserAction(GeoAnalyticsUserAction.OpenProgress);
        return this.services.dialog.showModal(() =>
                <GeoFreezePopupComponent
                    headerText1={this.services.translation.translate("GEO_FREEZE_POPUP_TOP_TEXT")}
                    headerText2={this.services.translation.translate("GEO_FREEZE_POPUP_MIDDLE_TEXT")}
                />, {
                title: this.services.translation.translate("GEO_DIALOG_STANDARD_TITLE"),
                icon: ({children, ...props}) => {
                    const LocationIcon = this.services.theme.currentTheme.icons.locationIcon;
                    return <LocationIcon {...props} animated={true} />
                },
                onClosed: () => {
                    this.analytics.reportUserAction(GeoAnalyticsUserAction.CloseProgress);
                },
                onHide: () => {
                    this.analytics.reportUserAction(GeoAnalyticsUserAction.HideProgress);
                },
                onDisplay: () => {
                    this.analytics.reportUserAction(GeoAnalyticsUserAction.DisplayProgress);
                },
                forceTransparentBackground: true,
                disableFullscreenMobile: true,
                forceCentralization: true,
                mobileCenterHeader: true,
                isHighPriority: true,
                hideCloseButton: true,
                forceZOrder: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.GEO_FREEZE_WINDOW] - 1
            }
        );
    }

    private _getGeoKickDialogMessages(kickReasonId: string, troubleShooterMessages: NullableUndefinedString | string[]): string | string[] {
        if(Check.isEmpty(troubleShooterMessages)) {
            return this.services.translation.translate('kick_reason_' + kickReasonId);
        }

        return troubleShooterMessages;

    }


    private _getGeoKickDialogButtons(buttons: GeoKickDialogButtons, resolve: (value: GeoKickDialogResult | PromiseLike<GeoKickDialogResult>) => void): ISimpleTextModalButtonOptions[] {
        if(buttons === GeoKickDialogButtons.OK) {
            return [
                {
                    text: this.services.translation.translate("OK"),
                    buttonPreset: "primary",
                    onClick: async () => {
                        this.analytics.reportUserAction(GeoAnalyticsUserAction.OK);
                        resolve(GeoKickDialogResult.OK);
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                }
            ]
        }

        const dialogButtons: ISimpleTextModalButtonOptions[] = [];

        if(buttons === GeoKickDialogButtons.RetryContactUs) {
            dialogButtons.push({
                text: this.services.translation.translate("RETRY"),
                buttonPreset: "primary",
                onClick: async () => {
                    this.analytics.reportUserAction(GeoAnalyticsUserAction.Retry);
                    resolve(GeoKickDialogResult.Retry);
                    return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                }
            });
        } else {
            dialogButtons.push({
                text: this.services.translation.translate("LOGOUT"),
                buttonPreset: "primary",
                onClick: async () => {
                    this.analytics.reportUserAction(GeoAnalyticsUserAction.Logout);
                    await this.services.user.logout();
                    resolve(GeoKickDialogResult.Logout);
                    return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                }
            });
        }

        dialogButtons.push({
            text: this.services.translation.translate("CONTACT"),
            buttonPreset: "secondary",
            onClick: async () => {
                this.analytics.reportUserAction(GeoAnalyticsUserAction.ContactUs);
                await this.services.appLauncher.openContactUs({
                    businessCorrelationID: _.getGuid(),
                    channel: {
                        source: CHANNEL_SOURCE.geoLocationRetry,
                        area: AreaType.button
                    }
                });
                resolve(GeoKickDialogResult.ContactUs);
                return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
            }
        });

        return dialogButtons;

    }

    showGeoKickDialog(options: {buttons: GeoKickDialogButtons;
                                kickReasonId: string;
                                troubleShooterMessages: NullableUndefinedString | string[];
                                isGenericKickMessage: boolean }): Promise<GeoKickDialogResult> {

        if(options.isGenericKickMessage) {
            this.analytics.reportUserAction(GeoAnalyticsUserAction.ShowGenericKickDialog);
        } else {
            this.analytics.reportUserAction(GeoAnalyticsUserAction.ShowKickDialog);
        }


        return new Promise<GeoKickDialogResult>(resolve => {
            this.services.dialog.showSimpleTextModal({
                title: this.services.translation.translate("GEO_DIALOG_STANDARD_TITLE"),
                text: this._getGeoKickDialogMessages(options.kickReasonId, options.troubleShooterMessages),
                buttons: this._getGeoKickDialogButtons(options.buttons, resolve),
                onClosedByXButton: () => {
                    this.analytics.reportUserAction(GeoAnalyticsUserAction.XClose);
                    resolve(GeoKickDialogResult.OK);
                }
            });
        })
    }
}